import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Tool } from 'shared/types';
import { ImageService } from '../../services/imageService';
import { createTool, updateTool, deleteTool, signToolImageUpload, putToSignedUrl } from '../../services/apiService';
import { prepareImageForUpload } from '../../utils/prepareImage';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { i18n } from '../../i18n/i18n';
import { useLanguage } from '../../context/LanguageContext';
import { getToolDisplayName } from '../../utils/toolLabels';
import { showToast } from '../kid/Toast';
import { PictureTile } from '../kid/KidUI';
import CameraModal from '../CameraModal';
import { EditorModal, useCloseGuard } from './EditorModal';

/** Admin → Tools: tools, tool form and delete (split out of AdminPanel.tsx, 2026-10-12). */
const ToolsTab: React.FC = () => {
  const { current_user, current_family, refreshFamilyData } = useAppContext() as any;
  const { current_language } = useLanguage();
  const [toolModal, setToolModal] = useState<{ isOpen: boolean; tool: Tool | null; isCreating: boolean }>({
    isOpen: false,
    tool: null,
    isCreating: false
  });
  const [removeToolModal, setRemoveToolModal] = useState<{ isOpen: boolean; toolId: string; toolName: string; error?: string; isDeleting?: boolean }>({
    isOpen: false, toolId: '', toolName: ''
  });
  const [tempToolData, setTempToolData] = useState<Partial<Tool>>({});
  const [tempToolImageURL, setTempToolImageURL] = useState<string | null>(null);
  const [tempToolFile, setTempToolFile] = useState<File | null>(null);
  const [toolImageURLs, setToolImageURLs] = useState<Record<string, string>>({});
  const [toolCameraModalOpen, setToolCameraModalOpen] = useState<boolean>(false);
  // Tools stored as image ids (older uploads) need their picture URL looked up.
  useEffect(() => {
    if (!current_family) return;
    let alive = true;
    (async () => {
      const urls: Record<string, string> = {};
      for (const tool of current_family.tools) {
        if (tool.image && !tool.image_url && !urls[tool.image]) {
          try { const url = await ImageService.getImageURL(tool.image); if (url) urls[tool.image] = url; }
          catch (error) { console.error('Failed to load tool image:', error); }
        }
      }
      if (alive) setToolImageURLs(urls);
    })();
    return () => { alive = false; };
  }, [current_family]);
  const getToolLabel = (tool: any): string => getToolDisplayName(tool, current_language);

  const handleConfirmDeleteTool = async () => {
    if (!removeToolModal.toolId) return;
    setRemoveToolModal(prev => ({ ...prev, isDeleting: true, error: undefined }));
    try {
      await deleteTool(removeToolModal.toolId);
      await refreshFamilyData();
      setRemoveToolModal({ isOpen: false, toolId: '', toolName: '' });
    } catch (error) {
      setRemoveToolModal(prev => ({ ...prev, isDeleting: false, error: (error as any)?.message || 'Failed to delete tool.' }));
    }
  };

  const handleCreateTool = async () => {
    try {
      if (!tempToolData.name?.trim()) {
        showToast({ message: i18n.t('admin_panel.err_tool_name'), tone: 'error' });
        return;
      }

      // Create first to obtain tool id
      const created = await createTool({
        name: tempToolData.name.trim()
      });

      if (tempToolFile) {
        const jpeg = await prepareImageForUpload(tempToolFile, 800); // HEIC → JPEG, shrunk
        const { upload_url, headers, public_url } = await signToolImageUpload(created.id, 'image/jpeg');
        await putToSignedUrl(upload_url, headers, jpeg);
        await updateTool(created.id, { name: tempToolData.name.trim(), image_url: public_url, image: null });
      }

      // Refresh family data to show the new tool
      await refreshFamilyData();
      
      setToolModal({ isOpen: false, tool: null, isCreating: false });
      setTempToolImageURL(null);
      setTempToolFile(null);
    } catch (error) {
      console.error('Failed to create tool:', error);
      showToast({ message: i18n.t('admin_panel.err_tool_create'), tone: 'error' });
    }
  };

  const handleUpdateTool = async () => {
    try {
      if (!toolModal.tool || !tempToolData.name?.trim()) {
        showToast({ message: i18n.t('admin_panel.err_tool_name'), tone: 'error' });
        return;
      }

      if (tempToolFile) {
        const jpeg = await prepareImageForUpload(tempToolFile, 800); // HEIC → JPEG, shrunk
        const { upload_url, headers, public_url } = await signToolImageUpload(toolModal.tool.id, 'image/jpeg');
        await putToSignedUrl(upload_url, headers, jpeg);
        await updateTool(toolModal.tool.id, { name: tempToolData.name.trim(), image_url: public_url, image: null });
      } else {
        await updateTool(toolModal.tool.id, {
          name: tempToolData.name.trim(),
          ...(tempToolData.image ? { image: tempToolData.image } : {})
        });
      }

      // Refresh family data to show the updated tool
      await refreshFamilyData();
      
      setToolModal({ isOpen: false, tool: null, isCreating: false });
      setTempToolImageURL(null);
      setTempToolFile(null);
    } catch (error) {
      console.error('Failed to update tool:', error);
      showToast({ message: i18n.t('admin_panel.err_tool_update'), tone: 'error' });
    }
  };


  const closeToolEditor = () => {
    setToolModal({ isOpen: false, isCreating: false, tool: null });
    if (tempToolImageURL) {
      URL.revokeObjectURL(tempToolImageURL);
      setTempToolImageURL(null);
    }
    setTempToolData({});
    setTempToolFile(null);
    setToolCameraModalOpen(false);
  };
  // ✕ / Esc / tap outside (2026-10-17): asks to save only when something was changed.
  const toolCloseGuard = useCloseGuard({
    open: toolModal.isOpen,
    snapshot: { tempToolData, file: tempToolFile ? `${tempToolFile.name}:${tempToolFile.size}:${tempToolFile.lastModified}` : null },
    save: () => (toolModal.isCreating ? handleCreateTool() : handleUpdateTool()),
    discard: closeToolEditor,
  });

  if (!current_family || !current_user) return null;
  return (
    <>
    <div className="mb-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <h3 className="font-kid-heading text-2xl font-extrabold text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.tool_management')}</h3>
        <button 
          className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50"
          onClick={() => {
            setTempToolData({});
            setToolModal({ isOpen: true, tool: null, isCreating: true });
          }}
        >
            + {i18n.t('admin_panel.create_new_tool')}
        </button>
      </div>
      
      {current_family.tools.length === 0 ? (
        <div className="bg-kid-card dark:bg-kid-card-dark rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark p-4 text-center text-kid-text-muted dark:text-kid-text-muted-dark text-lg italic">
          <p>{i18n.t('admin_panel.no_tools_msg')}</p>
        </div>
      ) : (
        // As many columns as fit with cards at least 150 px wide (2 on phones, ~4 on tablets in
        // portrait, 6–7 on wide screens); a few tools don't stretch into huge cards (auto-fill).
        <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 md:gap-4">
          {current_family.tools.map((tool: any) => (
            <div key={tool.id} className="flex flex-col bg-kid-card dark:bg-kid-card-dark rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark p-3">
              <div className="aspect-square w-full mb-2">
                <PictureTile src={tool.image_url || (tool.image && toolImageURLs[tool.image]) || null} label={getToolLabel(tool)} size="fill" rounded="rounded-kid-tile" />
              </div>
              <div className="text-base font-bold leading-tight text-kid-text dark:text-kid-text-dark mb-3 line-clamp-2 min-h-[2.5rem] break-words hyphens-auto" title={getToolLabel(tool)}>
                {getToolLabel(tool)}
              </div>
              <div className="mt-auto flex flex-col gap-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center min-h-[44px] px-3 rounded-full font-bold text-sm bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                  onClick={() => {
                    setTempToolData(tool);
                    setToolModal({ isOpen: true, tool, isCreating: false });
                  }}
                >
                  {i18n.t('admin_panel.edit')}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center justify-center min-h-[44px] px-3 rounded-full font-bold text-sm bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50"
                  onClick={() => setRemoveToolModal({ isOpen: true, toolId: tool.id, toolName: getToolLabel(tool) })}
                >
                  {i18n.t('admin_panel.remove')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
      {/* Tool Modal */}
      {toolModal.isOpen && (
        <EditorModal
          onRequestClose={toolCloseGuard.requestClose}
          title={toolModal.isCreating ? i18n.t('admin_panel.tool_modal_title_create') : `${i18n.t('admin_panel.tool_modal_title_edit_prefix')}: ${toolModal.tool?.name}`}
        >
            <div className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('library.tool_name')}</label>
                  <input 
                    type="text" 
                    placeholder={i18n.t('library.tool_name_ph')}
                    value={tempToolData.name || ''}
                    onChange={(e) => setTempToolData({ ...tempToolData, name: e.target.value })} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                  />
                </div>
                
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.tool_image_label')}</label>
                  {tempToolImageURL && (
                    <div className="w-full mb-2">
                      <img src={tempToolImageURL} alt="Tool preview" className="w-full h-32 object-cover rounded-kid-tile border-2 border-kid-border dark:border-kid-border-dark" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        if (file) {
                          if (tempToolImageURL) URL.revokeObjectURL(tempToolImageURL);
                          setTempToolFile(file);
                          setTempToolImageURL(URL.createObjectURL(file));
                          setTempToolData({ ...tempToolData, image: undefined });
                        }
                      }} 
                      className="flex-1 min-w-0 min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                    />
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                      onClick={() => setToolCameraModalOpen(true)}
                    >
                      {i18n.t('common.take_photo')}
                    </button>
                  </div>
                  {/* Tool Camera Modal */}
                  <CameraModal
                    isOpen={toolCameraModalOpen}
                    facingMode="environment"
                    title={i18n.t('admin_panel.camera_title') || 'Camera'}
                    onClose={() => setToolCameraModalOpen(false)}
                    onCapture={async (blob) => {
                      try {
                        if (tempToolImageURL) URL.revokeObjectURL(tempToolImageURL);
                        const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
                        setTempToolFile(file);
                        setTempToolImageURL(URL.createObjectURL(file));
                        setTempToolData(prev => ({ ...prev, image: undefined }));
                      } finally {
                        setToolCameraModalOpen(false);
                      }
                    }}
                  />
                  {tempToolData.image && !tempToolFile && (
                    <div className="mt-2">
                      <p className="text-sm text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.current_tool_image_label')}: {tempToolData.image}</p>
                      <button 
                        onClick={() => {
                          if (tempToolImageURL) {
                            URL.revokeObjectURL(tempToolImageURL);
                            setTempToolImageURL(null);
                          }
                          setTempToolData({ ...tempToolData, image: undefined });
                        }} 
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50 mt-2"
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 pt-5 border-t-2 border-kid-border dark:border-kid-border-dark">
                <button onClick={closeToolEditor} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50 flex-1">{i18n.t('admin_panel.cancel_btn')}</button>
                {toolModal.isCreating ? (
                  <button onClick={handleCreateTool} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 flex-1">{i18n.t('admin_panel.create_tool_btn')}</button>
                ) : (
                  <button onClick={handleUpdateTool} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 flex-1">{i18n.t('admin_panel.save_changes_btn')}</button>
                )}
              </div>
            </div>
          {toolCloseGuard.dialog}
        </EditorModal>
      )}
      <ConfirmDeleteModal
        isOpen={removeToolModal.isOpen}
        title={i18n.t('admin_panel.delete_tool_title')}
        message={<>{i18n.t('admin_panel.delete_confirm_prefix')} "<strong>{removeToolModal.toolName}</strong>"?</>}
        onConfirm={handleConfirmDeleteTool}
        onCancel={() => setRemoveToolModal({ isOpen: false, toolId: '', toolName: '' })}
        isDeleting={removeToolModal.isDeleting}
        error={removeToolModal.error}
      />
    </>
  );
};

export default ToolsTab;
